import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { api, apiError } from "@/lib/api";
import { EMPTY_FORM } from "@/components/integrations/constants";
import {
  IngestTab,
  LogsTab,
  RequiredTab,
  StatusTab,
} from "@/components/integrations/IntegrationTabs";

const TABS = [
  ["status", "Koppelingsstatus"],
  ["required", "Nog benodigde koppelingen"],
  ["ingest", "Handmatige / JSON-invoer"],
  ["logs", "API-logboek"],
];

export default function Integrations() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "ingest" ? "ingest" : "status");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [json, setJson] = useState("");
  const [catalogue, setCatalogue] = useState(null);

  const loadCatalogue = useCallback(async () => {
    try {
      const { data: stats } = await api.get("/taxonomy/stats");
      setCatalogue(stats);
    } catch {
      setCatalogue(null);
    }
  }, []);

  const refreshCatalogue = async () => {
    setBusy(true);
    setMessage("");
    setError("");
    try {
      const { data: result } = await api.post("/taxonomy/refresh");
      setCatalogue(result.stats);
      setMessage(`Voertuigcatalogus bijgewerkt: ${result.stats.makes} merken uit de marktplaats-taxonomie.`);
    } catch (e) {
      setError(apiError(e));
    }
    setBusy(false);
  };

  const load = useCallback(async () => {
    try {
      const { data: payload } = await api.get("/integrations");
      setData(payload);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
    loadCatalogue();
  }, [load, loadCatalogue]);

  const test = async (key) => {
    setBusy(true);
    setMessage("");
    try {
      const { data: status } = await api.post(`/integrations/${key}/test`);
      setMessage(`${status.label}: ${status.status_label}${status.message ? ` — ${status.message}` : ""}`);
      load();
    } catch (e) {
      setError(apiError(e));
    }
    setBusy(false);
  };

  const submitForm = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        mileage_km: form.mileage_km === "" ? null : Number(form.mileage_km),
        power_hp: form.power_hp === "" ? null : Number(form.power_hp),
        co2_wltp: form.co2_wltp === "" ? null : Number(form.co2_wltp),
        image_urls: form.image_urls.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
        equipment: form.equipment.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
      };
      const { data: result } = await api.post("/ingest/listings", { listings: [payload] });
      setMessage(`${result.stored} advertentie(s) opgeslagen, ${result.analysed} geanalyseerd, ${result.refreshed} bestaande analyses verversd.`);
      setForm(EMPTY_FORM);
    } catch (e) {
      setError(apiError(e));
    }
    setBusy(false);
  };

  const submitJson = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const parsed = JSON.parse(json);
      const listings = Array.isArray(parsed) ? parsed : [parsed];
      const { data: result } = await api.post("/ingest/listings", { listings });
      setMessage(`${result.stored} advertentie(s) opgeslagen, ${result.analysed} geanalyseerd, ${result.refreshed} bestaande analyses verversd.`);
      setJson("");
    } catch (e) {
      setError(e instanceof SyntaxError ? `Ongeldige JSON: ${e.message}` : apiError(e));
    }
    setBusy(false);
  };

  return (
    <AppShell
      title="Integraties"
      subtitle="Koppelingsstatus, benodigde toegang en handmatige/JSON-invoer van echte advertentiedata"
      freshness={[{ label: "Status gecontroleerd", value: data?.checked_at }]}
      actions={
        <button
          type="button"
          onClick={load}
          className="flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Verversen
        </button>
      }
    >
      <div className="mb-3 flex gap-1">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            data-testid={`integrations-tab-${key}`}
            onClick={() => setTab(key)}
            className={`rounded-sm border px-2.5 py-1.5 text-[12px] font-semibold transition-colors duration-150 ${
              tab === key
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {message && (
        <div className="mb-3 rounded-sm border border-emerald-300 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800" data-testid="integrations-message">
          {message}
        </div>
      )}
      {error && (
        <div className="mb-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-800" data-testid="integrations-error">
          {error}
        </div>
      )}

      {tab === "status" && (
        <StatusTab catalogue={catalogue} data={data} busy={busy} onRefreshCatalogue={refreshCatalogue} onTest={test} />
      )}
      {tab === "required" && <RequiredTab requiredAccess={data?.required_access} />}
      {tab === "ingest" && (
        <IngestTab form={form} setForm={setForm} json={json} setJson={setJson} busy={busy} onSubmitForm={submitForm} onSubmitJson={submitJson} />
      )}
      {tab === "logs" && <LogsTab logs={data?.logs} />}
    </AppShell>
  );
}
